/**
 * Utility function to safely extract error messages from API responses
 * Handles various error formats including FastAPI validation errors
 */
export const isAuthExpiryError = (error) => {
  if (!error) return false;
  if (error.isAuthExpired) return true;

  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const message = error?.response?.data?.message || error?.message || "";

  if (status === 401) {
    return (
      code === "AUTH_ACCESS_EXPIRED" ||
      code === "AUTH_NO_TOKEN" ||
      code === "AUTH_INVALID_TOKEN" ||
      message === "Access token expired" ||
      message === "Token expired" ||
      message === "Invalid refresh token" ||
      message === "No refresh token" ||
      message === "Invalid token type"
    );
  }

  return /refresh token/i.test(message);
};

export const getErrorMessage = (error, defaultMessage = 'An error occurred') => {
  // If error is already a string, return it
  if (typeof error === 'string') {
    return error;
  }

  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Check for axios error response
  if (error.response?.data?.error) {
    const detail = error.response.data.error?.message || error.response.data.error;
    
    // Handle array of validation errors (FastAPI Pydantic validation)
    if (Array.isArray(detail)) {
      return detail.map(err => {
        if (typeof err === 'object' && err.msg) {
          const location = err.loc ? err.loc.join(' > ') : 'Field';
          return `${location}: ${err.msg}`;
        }
        return typeof err === 'string' ? err : JSON.stringify(err);
      }).join(', ');
    }
    
    // Handle string error messages
    if (typeof detail === 'string') {
      return detail;
    }
    
    // Handle object error (shouldn't render directly in React)
    if (typeof detail === 'object') {
      // If it's a validation error object
      if (detail.msg) {
        const location = detail.loc ? detail.loc.join(' > ') : 'Field';
        return `${location}: ${detail.msg}`;
      }
      return JSON.stringify(detail);
    }
  }

  // Check for error.message
  if (error.message) {
    return error.message;
  }

  // Fallback to default message
  return defaultMessage;
};
